
import React, { useRef, useState } from 'react';
import { EditorContent } from '@tiptap/react';
import { useEditor } from './useEditor';
import EditorToolbar from './EditorToolbar';
import { useImageUploader } from './ImageUploader';
import MediaLibrary from './MediaLibrary';
import './EditorStyles.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const RichTextEditor = ({ value, onChange, placeholder = 'Start writing...' }: RichTextEditorProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  try {
    const editor = useEditor({
      value,
      onChange,
      placeholder,
    });

    const { uploadImage } = useImageUploader(editor);

    const handleImageUploadClick = () => {
      fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file && editor) {
        setIsUploading(true);
        try {
          await uploadImage(file);
        } catch (error) {
          console.error('Failed to upload image:', error);
        } finally {
          setIsUploading(false);
        }
        event.target.value = '';
      }
    };

    return (
      <div className="rich-text-editor border border-white/10 rounded-md bg-black/20 overflow-hidden">
        {editor && (
          <>
            <EditorToolbar 
              editor={editor} 
              onImageUploadClick={handleImageUploadClick}
              openMediaLibrary={() => setIsMediaLibraryOpen(true)}
              isUploading={isUploading}
            />
            <div className="editor-content-container p-4">
              <EditorContent editor={editor} className="min-h-[200px] prose prose-invert max-w-none" />
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
            
            <MediaLibrary
              isOpen={isMediaLibraryOpen}
              onOpenChange={setIsMediaLibraryOpen}
              editor={editor}
              onImageUpload={(file) => {
                setIsUploading(true);
                uploadImage(file)
                  .finally(() => setIsUploading(false));
              }}
              isUploading={isUploading}
            />
          </>
        )}
      </div>
    );
  } catch (error) {
    console.error('Error in RichTextEditor:', error);
    return (
      <div className="rich-text-editor border border-white/10 rounded-md bg-black/20 overflow-hidden p-4">
        <div className="text-red-500">Error loading editor: {error instanceof Error ? error.message : 'Unknown error'}</div>
      </div>
    );
  }
};

export default RichTextEditor;
